import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { X } from 'lucide-react';
import { useCreateClient, useUpdateClient } from '../../hooks/useClients';
import { Client, ClientStatus, PaymentStatus, CreateClientRequest, UpdateClientRequest } from '../../types';
import toast from 'react-hot-toast';

interface ClientFormProps {
  client?: Client;
  onClose: () => void;
  onSuccess: () => void;
}

type WorkspaceType = 'm365' | 'google' | 'hybrid';

export default function ClientForm({ client, onClose, onSuccess }: ClientFormProps) {
  const isEdit = !!client;

  const [formData, setFormData] = useState({
    companyName: client?.companyName || '',
    tenantId: client?.tenantId || '',
    status: client?.status || 'active' as ClientStatus,
    paymentStatus: client?.paymentStatus || 'current' as PaymentStatus,
    notes: client?.notes || '',
    workspaceType: (client?.workspaceType || 'm365') as WorkspaceType,
    googleAdminEmail: client?.googleAdminEmail || '',
    googleDomain: client?.googleDomain || '',
  });

  const { createClient, loading: creating, error: createError } = useCreateClient();
  const { updateClient, loading: updating, error: updateError } = useUpdateClient();

  const loading = creating || updating;
  const error = createError || updateError;

  const showGoogleFields = formData.workspaceType === 'google' || formData.workspaceType === 'hybrid';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEdit) {
        await updateClient({
          id: client.id,
          data: {
            companyName: formData.companyName,
            tenantId: formData.tenantId || undefined,
            status: formData.status,
            paymentStatus: formData.paymentStatus,
            notes: formData.notes,
            workspaceType: formData.workspaceType,
            googleAdminEmail: showGoogleFields ? (formData.googleAdminEmail || undefined) : undefined,
            googleDomain: showGoogleFields ? (formData.googleDomain || undefined) : undefined,
          } as UpdateClientRequest,
        });
        toast.success('Client updated successfully');
      } else {
        await createClient({
          companyName: formData.companyName,
          tenantId: formData.tenantId || undefined,
          status: formData.status,
          paymentStatus: formData.paymentStatus,
          notes: formData.notes,
          workspaceType: formData.workspaceType,
          googleAdminEmail: showGoogleFields ? (formData.googleAdminEmail || undefined) : undefined,
          googleDomain: showGoogleFields ? (formData.googleDomain || undefined) : undefined,
        } as CreateClientRequest);
        toast.success('Client created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(isEdit ? 'Failed to update client' : 'Failed to create client');
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-6 [&>button]:hidden">
        <div className="flex items-center justify-between mb-4">
          <DialogTitle className="text-lg font-medium text-foreground">
            {isEdit ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogDescription className="sr-only">Enter client details including company name, tenant ID, and status</DialogDescription>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Company Name *</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) =>
                setFormData({ ...formData, companyName: e.target.value })
              }
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Workspace Type</label>
            <select
              value={formData.workspaceType}
              onChange={(e) =>
                setFormData({ ...formData, workspaceType: e.target.value as WorkspaceType })
              }
              className="input"
            >
              <option value="m365">Microsoft 365</option>
              <option value="google">Google Workspace</option>
              <option value="hybrid">Hybrid (M365 + Google)</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Determines which tabs and features are available for this client
            </p>
          </div>

          {(formData.workspaceType === 'm365' || formData.workspaceType === 'hybrid') && (
          <div>
            <label className="label">Tenant ID (M365)</label>
            <input
              type="text"
              value={formData.tenantId}
              onChange={(e) =>
                setFormData({ ...formData, tenantId: e.target.value })
              }
              className="input"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Required for GDAP license sync
            </p>
          </div>
          )}

          {showGoogleFields && (
            <>
              <div>
                <label className="label">Google Admin Email</label>
                <input
                  type="email"
                  value={formData.googleAdminEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, googleAdminEmail: e.target.value })
                  }
                  className="input"
                  placeholder="admin@company.com"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Super admin account used for Google Workspace API access
                </p>
              </div>

              <div>
                <label className="label">Google Domain</label>
                <input
                  type="text"
                  value={formData.googleDomain}
                  onChange={(e) =>
                    setFormData({ ...formData, googleDomain: e.target.value })
                  }
                  className="input"
                  placeholder="company.com"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Primary Google Workspace domain
                </p>
              </div>
            </>
          )}

          <div>
            <label className="label">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as ClientStatus })
              }
              className="input"
            >
              <option value="active">Active</option>
              <option value="at-risk">At Risk</option>
              <option value="churned">Churned</option>
              <option value="pending-migration">Pending Migration</option>
            </select>
          </div>

          <div>
            <label className="label">Payment Status</label>
            <select
              value={formData.paymentStatus}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  paymentStatus: e.target.value as PaymentStatus,
                })
              }
              className="input"
            >
              <option value="current">Current</option>
              <option value="flagged">Flagged</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              className="input"
              rows={3}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
